import { useState, useEffect, useCallback } from 'react'

interface ImagePreloaderOptions {
  priority?: boolean
  timeout?: number
  retryCount?: number
  retryDelay?: number
}

interface ImageState {
  loaded: boolean
  error: boolean
  loading: boolean
}

export function useImagePreloader(
  src: string | null | undefined,
  options: ImagePreloaderOptions = {}
) {
  const {
    priority = false,
    timeout = 10000, // 10秒超时
    retryCount = 2,
    retryDelay = 1000
  } = options

  const [state, setState] = useState<ImageState>({
    loaded: false,
    error: false,
    loading: false
  })

  const [retries, setRetries] = useState(0)

  const loadImage = useCallback(async () => {
    if (!src) {
      setState({ loaded: false, error: false, loading: false })
      return
    }

    setState(prev => ({ ...prev, loading: true, error: false }))

    try {
      await new Promise<void>((resolve, reject) => {
        const img = new Image()
        const timeoutId = setTimeout(() => {
          reject(new Error('Image load timeout'))
        }, timeout)

        img.onload = () => {
          clearTimeout(timeoutId)
          resolve()
        }

        img.onerror = () => {
          clearTimeout(timeoutId)
          reject(new Error('Image load failed'))
        }

        img.src = src
      })

      setState({ loaded: true, error: false, loading: false })
      setRetries(0)
    } catch (error) {
      console.warn('Image load failed:', src, error)
      
      if (retries < retryCount) {
        setTimeout(() => {
          setRetries(prev => prev + 1)
        }, retryDelay)
      } else {
        setState({ loaded: false, error: true, loading: false })
      }
    }
  }, [src, timeout, retries, retryCount, retryDelay])

  useEffect(() => {
    if (priority) {
      loadImage()
    }
  }, [loadImage, priority])

  const startLoading = useCallback(() => {
    if (!state.loading && !state.loaded && !state.error) {
      loadImage()
    }
  }, [loadImage, state])

  return {
    ...state,
    startLoading,
    retry: () => {
      setRetries(0)
      loadImage()
    }
  }
}

// 批量预加载头像的 Hook
export function useBatchImagePreloader(
  images: (string | null | undefined)[],
  options: ImagePreloaderOptions & { batchSize?: number } = {}
) {
  const { batchSize = 3 } = options
  const [loadedCount, setLoadedCount] = useState(0)
  const [currentBatch, setCurrentBatch] = useState(0)

  const validImages = images.filter(Boolean) as string[]
  const totalBatches = Math.ceil(validImages.length / batchSize)

  useEffect(() => {
    if (currentBatch >= totalBatches) return

    const startIndex = currentBatch * batchSize
    const endIndex = Math.min(startIndex + batchSize, validImages.length)
    const batchImages = validImages.slice(startIndex, endIndex)

    const loadBatch = async () => {
      const promises = batchImages.map(src => {
        return new Promise<void>((resolve) => {
          const img = new Image()
          img.onload = () => {
            setLoadedCount(prev => prev + 1)
            resolve()
          }
          img.onerror = () => {
            resolve() // 即使失败也继续
          }
          img.src = src
        })
      })

      await Promise.all(promises)
      
      // 延迟加载下一批，避免阻塞主线程
      setTimeout(() => {
        setCurrentBatch(prev => prev + 1)
      }, 100)
    }

    loadBatch()
  }, [currentBatch, totalBatches, batchSize, validImages])

  return {
    loadedCount,
    totalImages: validImages.length,
    progress: validImages.length > 0 ? loadedCount / validImages.length : 1,
    isComplete: loadedCount >= validImages.length
  }
}
